import { useEffect, useRef, useState } from 'react';

import { getGeocoderInstance } from '../services/googleMaps/geocoder.service';

/**
 * Fetching address using Google Maps Geocoder \
 * Mapping results to the location data objects
 *
 * @param {Array<{
 * name: string;
 * value: Array<{ lat: string; long: string; }>;
 * }>} data Location data of suggestions from `useMapSearch` hook
 * @param {any} geocoder Google geocoder instance
 * @returns {Promise<[]>} Location data with mapped `humanReadableAddress` fields
 */
const fetchHumanReadableAddress = async (data, geocoder) => {
  if (!Array.isArray(data) || !data.length || geocoder === null) {
    return [];
  }

  return await Promise.all(
    data.map((location) => {
      const { value } = location;

      return new Promise((resolve) => {
        let humanReadableAddress = '';

        const locationCoords = {
          lat: Number.parseFloat(value[0].lat),
          lng: Number.parseFloat(value[0].long)
        };

        geocoder.geocode({ location: locationCoords }, (results, status) => {
          const okStatus = window.google.maps.GeocoderStatus.OK || 'OK';
          if (status === okStatus) {
            if (results[0]) {
              humanReadableAddress = results[0].formatted_address;
            } else {
              humanReadableAddress = 'No address found for these coordinates.';
            }
          } else {
            const errorMessage = 'Geocoder failed due to: ' + status;
            console.warn(errorMessage);
          }

          resolve({
            ...location,
            value: [
              {
                ...location.value[0],
                humanReadableAddress
              }
            ]
          });
        });
      });
    })
  );
};

/**
 * A React hook that fetches human-readable addresses from geographic coordinates using a Google Maps geocoding service. \
 * Converts latitude/longitude coordinates to human-readable addresses with automatic caching and loading state management.
 *
 * @param {{
 * name: string;
 * value: Array<{ lat: string; long: string; }>;
 * }} addresses
 * @returns {Array<any>}
 */
export const useFetchHumanReadableAddress = (addresses) => {
  const geocoder = getGeocoderInstance();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const prevAddressesRef = useRef(null);

  useEffect(() => {
    const argsChanged =
      !prevAddressesRef.current ||
      addresses.some((arg, i) => arg !== prevAddressesRef.current?.[i]);

    if (argsChanged) {
      setLoading(true);
      setError(null);

      fetchHumanReadableAddress(addresses, geocoder)
        .then(setData)
        .catch(setError)
        .finally(() => setLoading(false));

      prevAddressesRef.current = addresses;
    }
  }, [addresses, geocoder]);

  return { populatedWithAddresses: data, loading, error };
};
